import React from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin } from "lucide-react";
import { siteConfig } from "../siteConfig";
import LogoMark from "./LogoMark";
import { useQuoteModal } from "../context/QuoteModalContext";

/* ── Social button SVG icons ── */
const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

const InstagramIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="2" y="2" width="20" height="20" rx="5.5" fill="url(#ig-grad)"/>
    <circle cx="12" cy="12" r="4.5" stroke="white" strokeWidth="1.8"/>
    <circle cx="17.2" cy="6.8" r="1.2" fill="white"/>
    <defs>
      <radialGradient id="ig-grad" cx="30%" cy="107%" r="130%">
        <stop offset="0%" stopColor="#fdf497"/>
        <stop offset="20%" stopColor="#fd5949"/>
        <stop offset="45%" stopColor="#d6249f"/>
        <stop offset="80%" stopColor="#285AEB"/>
      </radialGradient>
    </defs>
  </svg>
);

const FacebookIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="24" height="24" rx="5" fill="#1877F2"/>
    <path d="M16 8h-2a1 1 0 0 0-1 1v2h3l-.5 3H13v7h-3v-7H8v-3h2V9a4 4 0 0 1 4-4h2v3z" fill="white"/>
  </svg>
);

/* Day pill — highlighted if today, closed shown dimmed */
const HourRow = ({ day, time }) => {
  const today = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][new Date().getDay()];
  const isToday = day === today;
  const isClosed = time.toLowerCase() === "closed";

  return (
    <div
      className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg text-sm font-semibold transition-colors"
      style={{
        backgroundColor: isToday ? siteConfig.colors.primary : "transparent",
        color: isToday ? "#fff" : isClosed ? "rgba(255,255,255,0.3)" : "rgba(255,255,255,0.6)",
      }}
    >
      <span className="font-black w-8 shrink-0">{day}</span>
      <span className={isClosed ? "italic" : ""}>{time}</span>
    </div>
  );
};

const Footer = () => {
  const { social = {}, hours = [] } = siteConfig;
  const { open: openModal } = useQuoteModal();

  return (
    <footer style={{ backgroundColor: siteConfig.colors.dark }} className="text-white pt-16 pb-8">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="grid sm:grid-cols-2 lg:grid-cols-[260px_1fr_1fr_1fr_1fr] gap-10 mb-12">

          {/* ── Brand card ── */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl overflow-hidden shadow-xl">

              {/* Card body */}
              <div className="px-6 pt-7 pb-5 flex flex-col items-center text-center">
                <div className="mb-3">
                  <LogoMark scrolled={true} />
                </div>
                <p className="font-display font-black text-base mb-1" style={{ color: siteConfig.colors.dark }}>
                  {siteConfig.businessName}
                </p>
                <a href={`mailto:${siteConfig.email}`} className="text-sm text-gray-500 hover:text-gray-800 transition-colors">
                  {siteConfig.email}
                </a>
                <a href={siteConfig.phoneHref} className="text-sm text-gray-500 hover:text-gray-800 transition-colors">
                  {siteConfig.phone}
                </a>
              </div>

              {/* Action buttons */}
              <div className="grid grid-cols-2">
                <Link
                  to="/contact"
                  className="flex items-center justify-center py-3.5 text-sm font-black uppercase tracking-wider text-white transition-opacity hover:opacity-90"
                  style={{ backgroundColor: siteConfig.colors.primary }}
                >
                  Contact
                </Link>
                <button
                  onClick={openModal}
                  className="flex items-center justify-center py-3.5 text-sm font-black uppercase tracking-wider border-t transition-colors hover:bg-gray-50"
                  style={{ color: siteConfig.colors.dark, borderColor: "#e5e7eb" }}
                >
                  Get Quote
                </button>
              </div>

              {/* Social icon buttons */}
              <div className="flex items-center justify-center gap-4 py-4 border-t border-gray-100">
                {social.google && (
                  <a
                    href={social.google}
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Google"
                    className="w-10 h-10 rounded-full border border-gray-200 flex items-center justify-center transition-all duration-200 hover:scale-110 hover:shadow-md"
                  >
                    <GoogleIcon />
                  </a>
                )}
                {social.instagram && (
                  <a
                    href={social.instagram}
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Instagram"
                    className="w-10 h-10 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110 hover:shadow-md"
                    style={{ background: "linear-gradient(135deg,#f9a825,#e91e63,#9c27b0)" }}
                  >
                    <InstagramIcon />
                  </a>
                )}
                {social.facebook && (
                  <a
                    href={social.facebook}
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Facebook"
                    className="w-10 h-10 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110 hover:shadow-md"
                    style={{ backgroundColor: "#1877F2" }}
                  >
                    <FacebookIcon />
                  </a>
                )}
              </div>

            </div>
          </div>

          {/* ── Services ── */}
          <div>
            <div className="font-bold text-xs uppercase tracking-widest mb-4 text-white/35">
              Services
            </div>
            <div className="flex flex-col gap-2">
              {siteConfig.services.map((s) => (
                <Link
                  key={s.slug}
                  to={`/services/${s.slug}`}
                  className="text-base text-white/60 hover:text-white transition-colors"
                >
                  {s.name}
                </Link>
              ))}
            </div>
          </div>

          {/* ── Service Areas ── */}
          <div>
            <div className="font-bold text-xs uppercase tracking-widest mb-4 text-white/35">
              Service Areas
            </div>
            <div className="flex flex-col gap-2">
              {siteConfig.serviceAreas.map((area) => (
                <Link
                  key={area}
                  to={`/service-areas/${area.toLowerCase().replace(/\s+/g, "-")}`}
                  className="text-base text-white/60 hover:text-white transition-colors"
                >
                  {area}, {siteConfig.state}
                </Link>
              ))}
            </div>
          </div>

          {/* ── Quick Links ── */}
          <div>
            <div className="font-bold text-xs uppercase tracking-widest mb-4 text-white/35">
              Quick Links
            </div>
            <div className="flex flex-col gap-2">
              {[
                { label: "Home",             to: "/" },
                { label: "About Us",         to: "/about" },
                { label: "Our Work",         to: "/gallery" },
                { label: "Reviews",          to: "/reviews" },
                { label: "Contact",          to: "/contact" },
                { label: "Privacy Policy",   to: "/privacy-policy" },
                { label: "Terms & Conditions", to: "/terms" },
              ].map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  className="text-base text-white/60 hover:text-white transition-colors"
                >
                  {l.label}
                </Link>
              ))}
            </div>
          </div>

          {/* ── Operating Hours ── */}
          <div>
            <div
              className="font-bold text-xs uppercase tracking-widest mb-4"
              style={{ color: siteConfig.colors.primary }}
            >
              Operating Hours
            </div>
            <div className="flex flex-col gap-0.5">
              {hours.map(({ day, time }) => (
                <HourRow key={day} day={day} time={time} />
              ))}
            </div>
          </div>

        </div>

        {/* ── Bottom bar ── */}
        <div className="border-t border-white/10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-white/30">
          <span>© {new Date().getFullYear()} {siteConfig.businessName}. All rights reserved.</span>
          <span>
            Website powered by{" "}
            <a
              href="https://soardigital.co"
              target="_blank"
              rel="noreferrer"
              className="text-white/50 hover:text-white transition-colors"
            >
              Soar Digital
            </a>
          </span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
